/**
 * Permission model (docs/product-design.md section 7, Authorization).
 *
 * The API's `Permission` enum is canonical. GET /admin/v1/me returns the
 * user's `roles` and `permissions`; the UI grants a permission when the API
 * lists it OR one of the user's roles implies it. The API remains the
 * enforcer; the UI only hides or disables what would fail.
 *
 *   permission            alias     viewer  editor  admin
 *   view_config           view        ✓       ✓       ✓    dashboards, config, message log
 *   view_message_bodies   bodies      ✓       ✓       ✓    rendered bodies and payloads
 *   edit_content          edit                ✓       ✓    templates, partials, event types, rules, simulate, test send
 *   manage_messages       operate             ✓       ✓    resend and cancel messages
 *   manage_config         manage                      ✓    applications, keys, identities, senders, webhooks, suppressions
 *   view_audit            audit                       ✓    the audit log
 *
 * Either name works with `can()` and `<Can>`; prefer the canonical name.
 */
import type { components } from '@/api/schema'

export type Permission = components['schemas']['Permission']
export type RoleName = components['schemas']['AdminRole']

export const PERMISSION_ALIASES = {
  view: 'view_config',
  bodies: 'view_message_bodies',
  edit: 'edit_content',
  operate: 'manage_messages',
  manage: 'manage_config',
  audit: 'view_audit',
} as const satisfies Record<string, Permission>

export type PermissionAlias = keyof typeof PERMISSION_ALIASES
export type PermissionName = Permission | PermissionAlias

export const PERMISSIONS: readonly Permission[] = [
  'view_config',
  'view_message_bodies',
  'edit_content',
  'manage_messages',
  'manage_config',
  'view_audit',
]

export const ROLE_PERMISSIONS: Record<RoleName, readonly Permission[]> = {
  viewer: ['view_config', 'view_message_bodies'],
  editor: ['view_config', 'view_message_bodies', 'edit_content', 'manage_messages'],
  admin: PERMISSIONS,
}

/** Resolve an alias ("edit") to its canonical permission ("edit_content"). */
export function resolvePermission(p: PermissionName): Permission {
  return (PERMISSION_ALIASES as Record<string, Permission>)[p] ?? (p as Permission)
}

/** "MailSmith.Admin" | "admin" | "ADMIN" -> "admin"; unknown roles -> undefined. */
export function normalizeRole(role: string): RoleName | undefined {
  const r = role
    .trim()
    .toLowerCase()
    .replace(/^mailsmith[._:-]/, '')
  return r === 'admin' || r === 'editor' || r === 'viewer' ? r : undefined
}

/** Canonical names or aliases, any case; unknown strings -> undefined. */
export function normalizePermission(p: string): Permission | undefined {
  const s = p.trim().toLowerCase()
  if ((PERMISSIONS as readonly string[]).includes(s)) return s as Permission
  return (PERMISSION_ALIASES as Record<string, Permission>)[s]
}

export interface Grants {
  roles: RoleName[]
  permissions: Set<Permission>
}

export function computeGrants(
  me: { roles?: readonly string[]; role?: string; permissions?: readonly string[] } | undefined,
): Grants {
  const rawRoles = [...(me?.roles ?? []), ...(me?.role ? [me.role] : [])]
  const roles = [...new Set(rawRoles.map(normalizeRole).filter((r): r is RoleName => !!r))]
  const permissions = new Set<Permission>()
  for (const r of roles) for (const p of ROLE_PERMISSIONS[r]) permissions.add(p)
  for (const raw of me?.permissions ?? []) {
    const p = normalizePermission(raw)
    if (p) permissions.add(p)
  }
  return { roles, permissions }
}

/** Highest role, for display. */
export function primaryRole(roles: RoleName[]): RoleName | undefined {
  return (['admin', 'editor', 'viewer'] as const).find((r) => roles.includes(r))
}
